/**
 * What a signed-in admin holds — the twin of vc_be utils/permissionCatalog.js.
 *
 * The server sends an admin's keys at sign-in. An admin saved before the
 * WhatsApp / demo-webinar / AI keys existed (no `permissionsRevision` on the
 * session) also holds them when they hold anything that changes something; a
 * view-only admin does not. That matters for a session that signed in before
 * the keys shipped: its stored list predates them, and without this rule its
 * screens would hide what the server still allows.
 *
 * Change both files together — __tests__/utils/permissionCatalog.test.js pins
 * the same cases as the server's.
 */

export const PERMISSIONS_REVISION = 1;

const READING = new Set(["access_audit_logs", "export_data", "distributor_view", "distributor_billing"]);

/** Keys that only let someone LOOK. */
export const isReadingKey = (key) => READING.has(key) || String(key).startsWith("view_");

/** Holds nothing that changes anything — view-only (or nothing at all). */
export const isViewOnly = (keys) => !(keys || []).some((k) => !isReadingKey(k));

const LEGACY_GRANTS = [
  { key: "reply_whatsapp" },
  { key: "manage_whatsapp" },
  { key: "manage_demo_webinars" },
  { key: "manage_ai", needs: "view_ai_attempts" },
];

/** The newer keys an admin saved before them holds. */
export function legacyGrants(keys) {
  const held = keys || [];
  if (isViewOnly(held)) return [];
  return LEGACY_GRANTS.filter((g) => !g.needs || held.includes(g.needs)).map((g) => g.key);
}

/** The keys a session user holds (not for full access — callers check that first). */
export function effectivePermissions(user) {
  const stored = Array.isArray(user?.permissions) ? user.permissions : [];
  if (Number(user?.permissionsRevision) >= PERMISSIONS_REVISION) return stored;
  return [...new Set([...stored, ...legacyGrants(stored)])];
}
